import { apiFetch } from "api/auth";

const API_BASE =
  process.env.REACT_APP_API_URL || "http://localhost:3000/api/v1";

interface ApiSuccessResponse<T> {
  success: true;
  message: string;
  data: T;
}

interface ApiErrorResponse {
  success: false;
  message?: string;
  error?: string;
}

async function parseResponse<T>(res: Response): Promise<T> {
  const json = (await res.json()) as ApiSuccessResponse<T> | ApiErrorResponse;
  if (!res.ok) {
    const msg =
      (json as ApiErrorResponse).message ||
      (json as ApiErrorResponse).error ||
      `Request failed (${res.status})`;
    throw new Error(msg);
  }
  return (json as ApiSuccessResponse<T>).data;
}

// ---------------------------------------------------------------------------
// List leads
// ---------------------------------------------------------------------------

export interface SurveillanceListItem {
  id: string;
  leadNo: string;
  clientName: string;
  email: string | null;
  phone: string | null;
  status: string;
  stage: string | null;
  markName: string | null;
  createdAt: string;
}

export interface SurveillanceListResult {
  items: SurveillanceListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export type SurveillanceStageFilter = "DISCUSSION" | "FILING" | "STATUS";
export type SurveillanceSort = "newest" | "oldest";

export interface ListSurveillanceLeadsParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  stage?: SurveillanceStageFilter;
  sort?: SurveillanceSort;
}

export async function listSurveillanceLeads(
  params: ListSurveillanceLeadsParams = {}
): Promise<SurveillanceListResult> {
  const q = new URLSearchParams();
  if (params.page != null) q.set("page", String(params.page));
  if (params.limit != null) q.set("limit", String(params.limit));
  if (params.search) q.set("search", params.search);
  if (params.status) q.set("status", params.status);
  if (params.stage) q.set("stage", params.stage);
  if (params.sort) q.set("sort", params.sort);

  const res = await apiFetch(
    `${API_BASE}/surveillance/leads?${q.toString()}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<SurveillanceListResult>(res);
}

// ---------------------------------------------------------------------------
// Next lead number
// ---------------------------------------------------------------------------

export async function getSurveillanceNextLeadNumber(): Promise<{
  leadNo: string;
}> {
  const res = await apiFetch(
    `${API_BASE}/surveillance/discussion/next-lead-number`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<{ leadNo: string }>(res);
}

// ---------------------------------------------------------------------------
// Get discussion details (edit / prefill mode)
// ---------------------------------------------------------------------------

export interface SurveillanceApplicationDetailsView {
  addresseeDesignation: string | null;
  addresseeFirmName: string | null;
  addresseePlace: string | null;
  formOfAddress: string | null;
}

export interface SurveillanceApplicationItemView {
  applicationId: string;
  mark: string | null;
  classNumber: number | null;
}

export interface SurveillanceDocumentView {
  documentId: string;
  fileKey: string;
  filename: string;
  downloadUrl: string;
}

export interface SurveillanceDiscussionDetails {
  leadId: string;
  leadNo: string;
  module: string;
  stage: string | null;
  initiationType: string | null;
  referenceLeadId: string | null;
  referenceLead: { id: string; leadNo: string; module: string } | null;
  preliminary: {
    markName: string | null;
    contactName: string;
    phone: string | null;
    email: string | null;
    callTime: string | null;
  };
  /** null = CLIENT_INITIATED; true = EXISTING_LEAD yes; false = EXISTING_LEAD no. */
  wantsToGoAhead: boolean | null;
  applicationDetails: SurveillanceApplicationDetailsView | null;
  numberOfApplications: number;
  applications: SurveillanceApplicationItemView[];
  suggestedApplications: SurveillanceApplicationItemView[];
  documents: SurveillanceDocumentView[];
}

export async function getSurveillanceDiscussionDetails(
  leadId: string
): Promise<SurveillanceDiscussionDetails> {
  const res = await apiFetch(
    `${API_BASE}/surveillance/discussion/${leadId}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<SurveillanceDiscussionDetails>(res);
}

// ---------------------------------------------------------------------------
// Create preliminary discussion
// ---------------------------------------------------------------------------

export type SurveillanceInitiationType = "CLIENT_INITIATED" | "EXISTING_LEAD";

export interface CreateSurveillanceDiscussionPayload {
  initiationType: SurveillanceInitiationType;
  // CLIENT_INITIATED required fields
  markName?: string;
  contactName?: string;
  phone?: string;
  callTime?: string;
  // Optional
  email?: string;
  // EXISTING_LEAD required fields
  referenceLeadId?: string;
  wantsToGoAhead?: boolean;
}

export interface CreateSurveillanceDiscussionResponse {
  leadId: string;
  leadNo: string;
  initiationType: SurveillanceInitiationType;
  referenceLeadId: string | null;
  wantsToGoAhead: boolean | null;
}

export async function createSurveillanceDiscussion(
  payload: CreateSurveillanceDiscussionPayload
): Promise<CreateSurveillanceDiscussionResponse> {
  const body: Record<string, unknown> = { ...payload };
  if (payload.initiationType === "CLIENT_INITIATED") {
    delete body.referenceLeadId;
    delete body.wantsToGoAhead;
  } else {
    delete body.markName;
    delete body.contactName;
    delete body.phone;
    delete body.callTime;
  }
  const res = await apiFetch(`${API_BASE}/surveillance/discussion`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return parseResponse<CreateSurveillanceDiscussionResponse>(res);
}

// ---------------------------------------------------------------------------
// Update preliminary discussion
// ---------------------------------------------------------------------------

export interface UpdateSurveillancePreliminaryPayload {
  markName?: string;
  contactName?: string;
  phone?: string;
  email?: string;
  callTime?: string;
  wantsToGoAhead?: boolean;
}

export async function updateSurveillancePreliminary(
  leadId: string,
  payload: UpdateSurveillancePreliminaryPayload
): Promise<{ leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/surveillance/discussion/${leadId}/preliminary`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ leadId: string }>(res);
}

// ---------------------------------------------------------------------------
// Step 2: Application Details from Client
// ---------------------------------------------------------------------------

export const SURVEILLANCE_ADDRESSEE_DESIGNATIONS = [
  "The Proprietor",
  "The Managing Partner",
  "The Designated Partner",
  "The Managing Director",
  "The Managing Trustee",
  "The Chairman",
  "NAME",
] as const;

export const SURVEILLANCE_FORM_OF_ADDRESS_OPTIONS = ["Sir", "Madam"] as const;

export const SURVEILLANCE_CLASS_NUMBERS = Array.from(
  { length: 45 },
  (_, i) => i + 1
);

export interface SurveillanceApplicationInput {
  mark?: string | null;
  classNumber?: number | null;
}

export interface SubmitSurveillanceApplicationDetailsPayload {
  addresseeDesignation?: string;
  addresseeFirmName?: string;
  addresseePlace?: string;
  formOfAddress?: string;
  applications?: SurveillanceApplicationInput[];
}

export async function submitSurveillanceApplicationDetails(
  leadId: string,
  payload: SubmitSurveillanceApplicationDetailsPayload
): Promise<{ leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/surveillance/discussion/${leadId}/application-details`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ leadId: string }>(res);
}

export interface SurveillanceUploadPresignedFile {
  key: string;
  bucket: string;
  url: string;
}

export async function generateSurveillanceUploadPresignedUrls(
  leadId: string,
  files: Array<{ filename: string; mimeType: string }>
): Promise<SurveillanceUploadPresignedFile[]> {
  const res = await apiFetch(
    `${API_BASE}/surveillance/discussion/${leadId}/documents/presign`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ files }),
    }
  );
  const data = await parseResponse<{ files: SurveillanceUploadPresignedFile[] }>(res);
  return data.files;
}

export async function registerSurveillanceDocuments(
  leadId: string,
  documents: Array<{ fileKey: string; filename: string }>
): Promise<{ leadId: string; registeredCount: number }> {
  const res = await apiFetch(
    `${API_BASE}/surveillance/discussion/${leadId}/documents`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ documents }),
    }
  );
  return parseResponse<{ leadId: string; registeredCount: number }>(res);
}

export async function deleteSurveillanceDocument(
  leadId: string,
  documentId: string
): Promise<{ leadId: string; documentId: string }> {
  const res = await apiFetch(
    `${API_BASE}/surveillance/discussion/${leadId}/documents/${documentId}`,
    { method: "DELETE", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<{ leadId: string; documentId: string }>(res);
}

// ---------------------------------------------------------------------------
// Workflow progress & skip
// ---------------------------------------------------------------------------

export interface SurveillanceProgressStep {
  slug: string;
  label: string;
  stage: string;
  stepType: string;
  parentStep?: string;
  isCompleted: boolean;
  isSkipped: boolean;
}

export interface SurveillanceProgressResponse {
  currentStep: string | null;
  currentSection: string | null;
  maxStepReached: string | null;
  steps: SurveillanceProgressStep[];
}

export async function getSurveillanceProgress(
  leadId: string
): Promise<SurveillanceProgressResponse> {
  const res = await apiFetch(
    `${API_BASE}/surveillance/discussion/${leadId}/progress`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<SurveillanceProgressResponse>(res);
}

export async function skipSurveillanceStep(
  leadId: string,
  step: string
): Promise<{ leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/surveillance/discussion/${leadId}/workflow/skip-step`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ step }),
    }
  );
  return parseResponse<{ leadId: string }>(res);
}

/** Backend SurveillanceStep enum slugs used for workflow progress lookup. */
export const SurveillanceWorkflowStep = {
  SV_S1_01_PreliminaryDiscussion: "SV_S1_01_PRELIMINARY_DISCUSSION",
  SV_S1_02_ApplicationDetailsFromClient:
    "SV_S1_02_APPLICATION_DETAILS_FROM_CLIENT",
  SV_S1_03_InvoiceToClient: "SV_S1_03_INVOICE_TO_CLIENT",
  SV_S1_04_PaymentFromClient: "SV_S1_04_PAYMENT_FROM_CLIENT",
} as const;

// ---------------------------------------------------------------------------
// Step 3: Invoice to Client for Surveillance
// ---------------------------------------------------------------------------

export interface SurveillanceInvoiceMarkPreview {
  id: string;
  mark: string | null;
  classNumber: number | null;
  slNo: number;
}

export interface SurveillanceInvoicePreview {
  addressee: string;
  addresseeDesignation: string | null;
  firmName: string | null;
  place: string | null;
  formOfAddress: string | null;
  marks: string[];
  selectedClasses: number[];
  noOfMarkClasses: number;
  profFeePerUnit: number;
  totalProfFee: number;
  discountProfFeeSurveillance: number;
  totalDiscount: number;
  invoiceAmount: number;
  invoiceNo: string | null;
  invoiceDate: string | null;
  includeInvoiceNo: boolean;
  collectedAmount: number;
  outstandingAmount: number;
  applications: SurveillanceInvoiceMarkPreview[];
  invoiceDocumentUrl: string | null;
  invoiceDocumentFilename: string | null;
}

export interface SurveillanceInvoiceEmailPreview {
  to: string | null;
  subject: string;
  body: string;
  filename: string | null;
  downloadUrl: string | null;
}

export interface SurveillanceInvoicePaymentMessagePreview {
  message: string;
  waLink: string;
  emailPreview: SurveillanceInvoiceEmailPreview;
}

export interface SaveSurveillanceInvoicePayload {
  invoiceDate?: string;
  discountProfFeeSurveillance?: number;
  includeInvoiceNo?: boolean;
  invoiceAmount?: number;
}

export interface SendSurveillanceInvoicePayload {
  sendWhatsApp?: boolean;
  sendEmail?: boolean;
}

export async function getSurveillanceInvoicePreview(
  leadId: string
): Promise<SurveillanceInvoicePreview> {
  const res = await apiFetch(
    `${API_BASE}/surveillance/invoice/${leadId}/to-client/preview`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<SurveillanceInvoicePreview>(res);
}

export async function saveSurveillanceInvoice(
  leadId: string,
  payload: SaveSurveillanceInvoicePayload
): Promise<SurveillanceInvoicePreview> {
  const res = await apiFetch(
    `${API_BASE}/surveillance/invoice/${leadId}/to-client/save`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<SurveillanceInvoicePreview>(res);
}

export async function generateSurveillanceInvoicePdf(
  leadId: string
): Promise<{ s3Key: string; downloadUrl: string; filename: string }> {
  const res = await apiFetch(
    `${API_BASE}/surveillance/invoice/${leadId}/to-client/generate`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<{ s3Key: string; downloadUrl: string; filename: string }>(res);
}

export async function sendSurveillanceInvoiceToClient(
  leadId: string,
  payload: SendSurveillanceInvoicePayload
): Promise<{ whatsAppLink?: string; emailSent?: boolean }> {
  const res = await apiFetch(
    `${API_BASE}/surveillance/invoice/${leadId}/to-client/send`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ whatsAppLink?: string; emailSent?: boolean }>(res);
}

export async function getSurveillanceInvoicePaymentMessagePreview(
  leadId: string
): Promise<SurveillanceInvoicePaymentMessagePreview> {
  const res = await apiFetch(
    `${API_BASE}/surveillance/invoice/${leadId}/to-client/payment-message-preview`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<SurveillanceInvoicePaymentMessagePreview>(res);
}

export async function sendSurveillanceInvoicePaymentMessage(
  leadId: string
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/surveillance/invoice/${leadId}/to-client/send-payment-message`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<{ waLink: string }>(res);
}

// ---------------------------------------------------------------------------
// Step 4: Payment from Client for Surveillance
// ---------------------------------------------------------------------------

export interface RecordSurveillancePaymentPayload {
  /** Delta — amount received in this payment, added to any prior payments. */
  collectedAmount: number;
}

export interface SurveillancePaymentRecordResponse {
  invoiceAmount: number;
  collectedAmount: number;
  outstandingAmount: number;
}

export async function recordSurveillancePaymentFromClient(
  leadId: string,
  payload: RecordSurveillancePaymentPayload
): Promise<SurveillancePaymentRecordResponse> {
  const res = await apiFetch(
    `${API_BASE}/surveillance/invoice/${leadId}/payment`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<SurveillancePaymentRecordResponse>(res);
}

// ---------------------------------------------------------------------------
// Status stage
// ---------------------------------------------------------------------------

export interface SurveillanceConflictingMarkEntry {
  conflictNo: number;
  mark: string;
  applicationNumber: string;
  applicant: string;
  applicantPlace: string;
  status: string;
}

export interface SurveillanceStatusContext {
  detailedSearchDone: boolean;
  detailedSearchDoneAt: string | null;
  addressee: string | null;
  addresseeDesignation: string | null;
  addresseeFirmName: string | null;
  addresseePlace: string | null;
  formOfAddress: string | null;
  marks: string | null;
  selectedClasses: string | null;
  reportDate: string | null;
  conflictingMarks: SurveillanceConflictingMarkEntry[];
  numberOfConflicts: number;
  pdf: { key: string; url: string; filename: string } | null;
  pdfIsStale: boolean;
  pdfGeneratedAt: string | null;
  pdfGeneratedBy: string | null;
  noteSentAt: string | null;
}

export async function getSurveillanceStatusContext(
  leadId: string
): Promise<SurveillanceStatusContext> {
  const res = await apiFetch(
    `${API_BASE}/surveillance/status/${leadId}/context`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<SurveillanceStatusContext>(res);
}

export async function markSurveillanceDetailedSearchDone(
  leadId: string
): Promise<SurveillanceStatusContext & { leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/surveillance/status/${leadId}/detailed-search`,
    { method: "PATCH", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<SurveillanceStatusContext & { leadId: string }>(res);
}

export interface SaveSurveillanceNoteFieldsPayload {
  reportDate?: string | null;
  conflictingMarks?: SurveillanceConflictingMarkEntry[] | null;
}

export async function saveSurveillanceNoteFields(
  leadId: string,
  payload: SaveSurveillanceNoteFieldsPayload
): Promise<SurveillanceStatusContext & { leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/surveillance/status/${leadId}/note`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<SurveillanceStatusContext & { leadId: string }>(res);
}

export async function generateSurveillanceNotePdf(
  leadId: string,
  force?: boolean
): Promise<{ key: string; url: string; filename: string; leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/surveillance/status/${leadId}/note/generate`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ force: force ?? false }),
    }
  );
  return parseResponse<{ key: string; url: string; filename: string; leadId: string }>(res);
}

export interface SendSurveillanceNotePayload {
  sendWhatsApp?: boolean;
  sendEmail?: boolean;
}

export interface SendSurveillanceNoteResult {
  whatsAppLink?: string;
  emailSent?: boolean;
  leadId: string;
}

export async function sendSurveillanceNoteToClient(
  leadId: string,
  payload: SendSurveillanceNotePayload
): Promise<SendSurveillanceNoteResult> {
  const res = await apiFetch(
    `${API_BASE}/surveillance/status/${leadId}/note/send`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<SendSurveillanceNoteResult>(res);
}
